// src/modules/system/components/ErrorFilters.js
//
// Filter bar for the System Errors list. Every control is a controlled input
// driven by the parent's `filters` object; the parent owns the debounce for
// `search` so typing does not fire a request per keystroke.
import React from 'react';
import styled from 'styled-components';
import Select from '../../../components/shared/Select';
import {
  SEVERITIES, STATUSES, SOURCES, PLATFORMS, sourceLabel, statusLabel,
} from '../errorLogService';

const Filters = styled.div`
  display: flex; flex-wrap: wrap; gap: ${(p) => p.theme.spacing(2)};
  background: ${(p) => p.theme.colors.surface}; border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(2)}; margin-bottom: ${(p) => p.theme.spacing(3)};
  align-items: flex-end;

  /* Eight filters at 140-220px each wrap into a ragged five-row block on a
     phone. Two even columns keep it to three tidy rows, with Search full-width
     because it is the one people actually use on mobile. */
  @media (max-width: 900px) {
    display: grid; grid-template-columns: 1fr 1fr;
    gap: ${(p) => p.theme.spacing(1)};
    padding: ${(p) => p.theme.spacing(1.5)};
    margin-bottom: ${(p) => p.theme.spacing(2)};
    > * { min-width: 0 !important; }
    /* The shared Select renders 20px padding + a 45px icon gutter — far too
       tall to stack eight of. */
    select { padding: 12px 12px 12px 38px !important; margin: 4px 0 !important;
             font-size: 14px !important; }
    svg { font-size: 1rem !important; left: 12px !important; }
  }
`;
const FullWidth = styled.div`
  @media (max-width: 900px) { grid-column: 1 / -1; }
`;
const Field = styled.div`
  display: flex; flex-direction: column; gap: 4px; min-width: ${(p) => p.$wide ? '220px' : '150px'};
  flex: ${(p) => p.$wide ? 1 : 'initial'};
  label { color: ${(p) => p.theme.colors.textSecondary}; font-size: ${(p) => p.theme.typography.fontSize.small}; }
  input { background: ${(p) => p.theme.colors.background}; color: ${(p) => p.theme.colors.textPrimary};
    border: 1px solid ${(p) => p.theme.colors.inputGray}; border-radius: ${(p) => p.theme.borderRadius};
    padding: ${(p) => p.theme.spacing(1)} ${(p) => p.theme.spacing(2)}; width: 100%; }
`;
const ClearBtn = styled.button`
  background: transparent; color: ${(p) => p.theme.colors.textSecondary};
  border: 1px solid ${(p) => p.theme.colors.inputGray}; border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(1)} ${(p) => p.theme.spacing(2)};
  cursor: pointer; align-self: center; white-space: nowrap;
  &:hover { color: ${(p) => p.theme.colors.primary}; }
  @media (max-width: 900px) { width: 100%; padding: 10px; }
`;

const ErrorFilters = ({ filters, onChange, onClear, appVersions = [] }) => {
  const set = (key) => (e) => onChange(key, e.target.value);

  return (
    <Filters>
      <FullWidth>
        <Field $wide>
          <label>Search</label>
          <input
            type="text"
            placeholder="Message, type, route, fingerprint…"
            value={filters.search}
            onChange={set('search')}
          />
        </Field>
      </FullWidth>

      <div style={{ minWidth: 150 }}>
        <Select label="Source" name="source" value={filters.source} onChange={set('source')}>
          <option value="">All sources</option>
          {SOURCES.map((s) => <option key={s} value={s}>{sourceLabel(s)}</option>)}
        </Select>
      </div>

      <div style={{ minWidth: 140 }}>
        <Select label="Platform" name="platform" value={filters.platform} onChange={set('platform')}>
          <option value="">All platforms</option>
          {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
        </Select>
      </div>

      <div style={{ minWidth: 140 }}>
        <Select label="Severity" name="severity" value={filters.severity} onChange={set('severity')}>
          <option value="">Error &amp; above</option>
          {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
        </Select>
      </div>

      <div style={{ minWidth: 150 }}>
        <Select label="Status" name="status" value={filters.status} onChange={set('status')}>
          <option value="">Not ignored</option>
          {STATUSES.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
        </Select>
      </div>

      {/* Populated from the stats byAppVersion breakdown — the fastest way to
          answer "did the last TestFlight build cause this?". */}
      {appVersions.length > 0 && (
        <div style={{ minWidth: 150 }}>
          <Select label="App version" name="app_version" value={filters.app_version} onChange={set('app_version')}>
            <option value="">All versions</option>
            {appVersions.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </div>
      )}

      <Field><label>From</label>
        <input type="date" value={filters.from} onChange={set('from')} /></Field>
      <Field><label>To</label>
        <input type="date" value={filters.to} onChange={set('to')} /></Field>

      <FullWidth>
        <ClearBtn type="button" onClick={onClear}>Clear filters</ClearBtn>
      </FullWidth>
    </Filters>
  );
};

export default ErrorFilters;
